/**
 * DesktopNotificationPrompt
 *
 * Bottom-fixed banner asking vendors to enable browser desktop notifications.
 * Slides up from the bottom once CookieConsent has been resolved.
 *
 * - "OK" → calls onAllow (triggers browser permission dialog)
 * - "No, thanks" → calls onDismiss (permanent, stored in localStorage)
 * - "×" → calls onSessionDismiss (session-only, stored in sessionStorage)
 */

import { useRef, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Slide } from '@mui/material';
import {
  NotificationsActive as NotifIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface DesktopNotificationPromptProps {
  open: boolean;
  onAllow: () => void;
  /** Permanent dismiss — "No, thanks" button. */
  onDismiss: () => void;
  /** Session-only dismiss — "×" button. */
  onSessionDismiss: () => void;
}

export function DesktopNotificationPrompt({
  open,
  onAllow,
  onDismiss,
  onSessionDismiss,
}: DesktopNotificationPromptProps) {
  const okButtonRef = useRef<HTMLButtonElement>(null);

  // Move focus to the "OK" button when the banner appears so keyboard users
  // can act immediately. Restore focus on dismiss via the parent handler.
  useEffect(() => {
    if (open) {
      // Small delay so the Slide animation has time to mount the element.
      const id = setTimeout(() => okButtonRef.current?.focus(), 100);
      return () => clearTimeout(id);
    }
  }, [open]);

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Box
        role="region"
        aria-label="Desktop notification permission"
        aria-live="polite"
        sx={{
          position: 'fixed',
          // On mobile, sit above the BottomNavigation (64px + safe-area inset).
          // On desktop, anchor to the bottom edge.
          bottom: { xs: 'calc(64px + env(safe-area-inset-bottom))', sm: 0 },
          left: 0,
          right: 0,
          bgcolor: '#112240',
          borderTop: '1px solid #233554',
          p: 2,
          // zIndex 1049 — below CookieConsent (1050) so legal consent is never obscured.
          // CookieConsent is resolved before this banner is shown (see VendorDesktopNotificationPrompt).
          zIndex: 1049,
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {/* Left: icon + copy */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
            <NotifIcon sx={{ color: 'success.main', fontSize: 28, mt: 0.25, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                Get notified when booked sessions are about to expire
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Stay on top of active sessions even when you're in another tab.
              </Typography>
            </Box>
          </Box>

          {/* Right: action buttons + close */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Button
              ref={okButtonRef}
              variant="contained"
              size="small"
              onClick={onAllow}
              color="success"
            >
              OK
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={onDismiss}
              sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
            >
              No, thanks
            </Button>
            <IconButton
              size="small"
              onClick={onSessionDismiss}
              aria-label="Dismiss notification prompt"
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Slide>
  );
}
